<template>
  <div class="all-ease">
    <div class='px-24 mb-px-16 overflow-auto' :style='isDesktop || formData.isUIPopup ? `height: ${formData.heightForm}px` : ""'>
      <div :class="formData.labelClass?.stepsLabel">Step {{ indexCurrentStep }} of 5</div>
      <div :class="formData.labelClass?.titleClass">Travellers & Accommodation</div>
      <div :class="[formData.labelClass?.subTitleClass, formData.labelClass?.divider]">
        How many people are travelling, and what style of accommodation are you looking for?
      </div>
      <div class="py-16">
        <div class="relative">
          <form class="content-form">
            <label :class="formData.labelClass?.cssLabelField" ref="numOfTravellersRef">How many people are travelling?</label>
            <div :class="formData.labelClass?.cssLabelField">
              <Input
                type="number"
                placeholder="0"
                :minNumber="0"
                v-model="dataStep.pax"
                @blur="validateInputNumberOfTraveller"
                :extraClass="formData.labelClass?.inputExtraClass"
              />
              <div class="error-message" v-if="(errors as any).pax">Please indicate the number of travellers</div>
            </div>
            <label :class="formData.labelClass?.cssLabelField" ref="accommodationRef">Accommodation preference</label>
            <div :class="formData.labelClass?.cssLabelField">
              <CheckBox
                v-for="(item, index) in checkBoxList"
                :key="index"
                :id="`checkbox-${index}`"
                :name="`checkbox-${item.label}`"
                :label="item.label"
                :value="item.value"
                v-model="dataStep.accm_pref"
                classCustom="mb-8"
              />
              <div class="error-message" v-if="(errors as any).accommodation">Please select a preference</div>
            </div>
            <div class="all-ease">
                <label :class="formData.labelClass?.cssLabelField">
                  Ideal room configuration <span :class="formData.labelClass?.cssLabelOptionField">(*optional)</span>
                </label>
                <div :class="formData.labelClass?.cssLabelField">
                  <textarea
                    placeholder="..."
                    v-model="dataStep.ideal_room_config"
                    class="db wp-100 h-80 pt-13 pb-11 pl-16 pr-40 br-6 bd-1 bd-gray-medium fs-14 fw-400 lh-17 wp-100 outline-0"
                  />
                </div>
                <span class="gray-700-brand fs-16 fw-400 lh-24 mt-16 mb-12 db mb-fs-14 mb-lh-20">
                  Tell us about your travelling companions and your accommodation preferences (Single, Twin, Family Room) so we can give you the best price
                  estimate for your trip.
                </span>
            </div>
            <div v-if="error" class='pb-30 red error-bottom'>{{ error }}</div>
          </form>
        </div>
      </div>
    </div>
    <FooterSteps
      :disableSubmitButton="disableSubmitButton"
      :labelClass="formData.labelClass"
      :stepActive="EnquiryStepEnum.How"
      :isUIPopup='formData.isUIPopup'
      :button-id="`enquiry-form-button-continue-step${indexCurrentStep}`"
      @nextStep="nextStep"
      @prevStep="$emit('prevStep')"
    />
  </div>
</template>
<script setup lang="ts">
import { ref, computed, watch, reactive, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import FooterSteps from '~/components/pages/Enquiry/MultiStepsForm/Steps/FooterSteps.vue'
import CheckBox from '~/components/ui/checkbox/CheckBox.vue'
import Input from '~/components/ui/input/Input.vue'
import { DESKTOP } from '~/constant/common'
import { EnquiryStepEnum } from '~/enum/enquiry'
import { isDeepEqual } from '~/helper'
import { getEnquiryID } from '~/helper/enquiry'
import useFormValidation from '~/utils/validators'
import { debounceAmplitudeScriptTracking } from '~/mixins/amplitude'
import { useEnquiryCaptureStore } from '~/store/pinia/enquiryCapture'
import { useGeneralInfoStore } from '~/store/pinia/generalInfo'

export interface CheckBoxType {
  value: string | number
  label: string
}

const checkBoxList: CheckBoxType[] = [
  { value: '5-star', label: '5-Star' },
  { value: '4-star', label: '4-Star' },
  { value: '3-star', label: '3-Star' },
]

// props & emits
defineProps({
  formData: { type: Object, default: () => {} },
})
const emits = defineEmits(['nextStep', 'prevStep'])

// define
const enquiryCaptureStore = useEnquiryCaptureStore()
const route = useRoute()
const { validateNumberField, errors } = useFormValidation()
const generalStore = useGeneralInfoStore()

const dataStep = reactive({
  pax: null as null | number,
  accm_pref: [checkBoxList[1].value] as Array<string | number>, //default value 4-start
  ideal_room_config: '',
})
const numOfTravellersRef = ref<HTMLElement | null>(null)
const accommodationRef = ref<HTMLElement | null>(null)
const disableSubmitButton = ref(false)
const error = ref('')

// computed
// const showIdealRoom = computed(() => dataStep.pax! >= 2)
const arrayStepEnquiry = computed(() => enquiryCaptureStore.arrayStep)
const windowWidth = computed(() => generalStore.generalData.windowWidth)
const isDesktop = computed(() => windowWidth.value > DESKTOP)

const stepOld = computed(() => enquiryCaptureStore.stepsData)
const stepBefore = computed(() => {
  if (stepOld.value && stepOld.value[route.path.toString()] && stepOld.value[route.path.toString()][EnquiryStepEnum.How])
    return stepOld.value[route.path.toString()][EnquiryStepEnum.How]
  return {}
})
const indexCurrentStep = computed(() => arrayStepEnquiry.value.indexOf(EnquiryStepEnum.How) + 1)

// method
function validateInputNumberOfTraveller() {
  validateNumberField('pax', dataStep.pax)
}

function validateAccommodation() {
  ;(errors as any).accommodation = !dataStep.accm_pref.length
}

function validationField() {
  validateInputNumberOfTraveller()
  validateAccommodation()

  if ((errors as any).pax) {
    numOfTravellersRef.value!.scrollIntoView({ behavior: 'smooth' })
    return true
  }
  if ((errors as any).accommodation) {
    accommodationRef.value!.scrollIntoView({ behavior: 'smooth' })
    return true
  }
  return (errors as any).pax || (errors as any).accommodation
}

const nextStep = async () => {
  if (validationField()) return
  try {
    disableSubmitButton.value = true
    const payload = {
      path: route.path.toString(),
      step: EnquiryStepEnum.How,
      [EnquiryStepEnum.How]: {
        ...dataStep,
        accm_pref: dataStep.accm_pref.join(','),
      },
    }
    const stepEdit = isDeepEqual(stepBefore.value, dataStep)

    if (!stepEdit) {
      const step = await enquiryCaptureStore.submitEnquiryCapture({ payload, dataStep })
      if (step?.status === 200) {
        debounceAmplitudeScriptTracking("View Enquiry (step 4)", {
          enquiry_id:  getEnquiryID(route.path)

        });
        error.value = ''
        emits('nextStep')
      } else {
        error.value = step?.message || ''
        throw new Error(`submit enquiry step ${EnquiryStepEnum.How} error (data):`, {
          cause: {
            data: step,
            payload: payload
          }
        })
      }
    } else if(!error.value) {
      emits('nextStep')
    }
  } catch (error) {
    console.log({ error })
    throw new Error(`submit enquiry step ${EnquiryStepEnum.How} error:`, { cause: error })
  } finally {
    disableSubmitButton.value = false
  }
}

watch(
  () => dataStep.pax,
  (v) => {
    ;(errors as any).pax = false
    if (v && v <= 1) dataStep.ideal_room_config = ''
  },
)

watch(
  () => dataStep.accm_pref,
  () => ((errors as any).accommodation = false),
)

onMounted(() => {
  const data = stepBefore.value
  if (data) {
    if(data?.pax) dataStep.pax = data?.pax
    dataStep.accm_pref = data?.accm_pref && data?.accm_pref.length ? data?.accm_pref : dataStep.accm_pref
    if(data?.ideal_room_config) dataStep.ideal_room_config = data?.ideal_room_config
  }
})
</script>
<style lang="scss" scoped></style>
